import React, { useState, useEffect, useRef } from "react";
import { Typography, Paper, Box, Stack, Chip, Fade, useTheme, alpha, IconButton, Collapse } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { LightTooltip } from "./LightTooltip";
import WordDetail from "@/pages/LearnPage/components/WordSearch/WordDetail";
import SentencePronunciation from "./SentencePronunciation";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { colorByLevel, BASE_DICT_SOUND_UK, BASE_DICT_SOUND_US } from "@/constants";
import { onPlayReadStr } from "@/helpers";
import { IPronunciationAnalyzeOutput } from "@/services-react-query/api/practice.service";
import { useSelector } from "react-redux";
import { IApplicationState } from "@/store-redux";

interface MainIdeaData {
  main_idea: string;
  keywords: string[];
}

interface MainIdeaTableProps {
  data: MainIdeaData[];
  sentences: string[];
  enableShadowing: boolean;
  enablePronunciation: boolean;
  showWordTooltip: boolean;
  showVocaLevel: boolean;
  dataWordSearch: any;
  isFetchedWordSearch: boolean;
  listLabels: string[];
  pronunciationResults: Record<number, IPronunciationAnalyzeOutput>;
  onPronunciationResult: (sentenceIndex: number, result: IPronunciationAnalyzeOutput) => void;
}

const MainIdeaTable: React.FC<MainIdeaTableProps> = ({
  data,
  sentences,
  enableShadowing,
  enablePronunciation,
  showWordTooltip,
  showVocaLevel,
  dataWordSearch,
  isFetchedWordSearch,
  listLabels,
  pronunciationResults,
  onPronunciationResult,
}) => {
  const theme = useTheme();
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isDark = appMode === "dark";

  // Resizable column width percentage state (default 58% left, 42% right)
  const [leftWidth, setLeftWidth] = useState<number>(58);
  const isDraggingRef = useRef<boolean>(false);
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [expandedPronunciationIdx, setExpandedPronunciationIdx] = useState<number | null>(0);

  const getScoreColor = (score: number) => {
    if (score >= 85) return "#2e7d32";
    if (score >= 70) return "#1976d2";
    if (score >= 55) return "#ef6c00";
    return "#c62828";
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDraggingRef.current = true;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      const relativeX = e.clientX - rect.left;
      const newPercent = (relativeX / rect.width) * 100;
      // Clamp left width between 28% and 72%
      const clamped = Math.min(72, Math.max(28, newPercent));
      setLeftWidth(clamped);
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handlePlayWordSound = (wordData: any, type: "us" | "uk" = "us") => {
    if (!wordData) return;
    if (!wordData.uid) {
      onPlayReadStr(wordData.word, undefined, type === "us" ? "en-US" : "en-GB");
      return;
    }
    const baseSoundByType: { [key: string]: string } = {
      us: BASE_DICT_SOUND_US,
      uk: BASE_DICT_SOUND_UK,
    };
    const soundUrl = `${baseSoundByType[type]}${wordData.uid}.mp3`;
    const audio = new Audio(soundUrl);
    audio.play().catch((err) => console.error("Error playing audio:", err));
  };

  return (
    <Fade in timeout={800}>
      <Box ref={tableRef} id="main-idea-table" sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
        {sentences.map((sen, index) => {
          const item = data[index];
          const isExpanded = expandedPronunciationIdx === index;
          const existingScore = pronunciationResults[index]?.overallScore;

          // If enablePronunciation is TRUE, render clean collapsible accordion per sentence
          if (enablePronunciation) {
            return (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  borderRadius: "20px",
                  background: isDark
                    ? isExpanded
                      ? alpha(theme.palette.background.paper, 0.6)
                      : alpha(theme.palette.background.paper, 0.25)
                    : isExpanded
                      ? "#ffffff"
                      : alpha("#ffffff", 0.75),
                  backdropFilter: "blur(20px)",
                  border: isExpanded
                    ? `2px solid ${alpha(theme.palette.primary.main, 0.45)}`
                    : `1px solid ${isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(102, 126, 234, 0.12)"}`,
                  boxShadow: isExpanded
                    ? isDark
                      ? "0 10px 30px rgba(0,0,0,0.35)"
                      : "0 10px 30px rgba(102, 126, 234, 0.12)"
                    : "0 2px 8px rgba(0,0,0,0.02)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  overflow: "hidden",
                }}
              >
                {/* Clickable Header Accordion Bar */}
                <Box
                  onClick={() => setExpandedPronunciationIdx(isExpanded ? null : index)}
                  sx={{
                    p: { xs: 1.6, md: 2 },
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    userSelect: "none",
                    bgcolor: isExpanded
                      ? isDark
                        ? "rgba(102, 126, 234, 0.06)"
                        : "rgba(102, 126, 234, 0.03)"
                      : "transparent",
                    "&:hover": {
                      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(102, 126, 234, 0.05)",
                    },
                  }}
                >
                  {/* Left: Index + Label + Preview Text */}
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "8px",
                        bgcolor: isExpanded ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.12),
                        color: isExpanded ? "#fff" : theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "13.5px",
                        flexShrink: 0,
                        transition: "all 0.25s ease",
                      }}
                    >
                      {index + 1}
                    </Box>

                    {listLabels?.[index] && (
                      <Chip
                        label={listLabels[index]}
                        size="small"
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          fontWeight: 700,
                          fontSize: "11px",
                          height: "22px",
                          flexShrink: 0,
                        }}
                      />
                    )}

                    <Typography
                      sx={{
                        fontSize: { xs: "15.5px", md: "17px" },
                        fontWeight: 750,
                        color: isDark ? "#ffffff" : "#0f172a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: isExpanded ? "normal" : "nowrap",
                        lineHeight: 1.5,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {sen}
                    </Typography>
                  </Stack>

                  {/* Right: Score (if analyzed) + Action Indicator */}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {existingScore !== undefined && (
                      <Chip
                        label={`${Math.round(existingScore)} / 100`}
                        size="small"
                        sx={{
                          fontWeight: 800,
                          fontSize: "11px",
                          bgcolor: getScoreColor(existingScore),
                          color: "#fff",
                          height: "22px",
                        }}
                      />
                    )}

                    <Chip
                      icon={isExpanded ? undefined : <RecordVoiceOverIcon sx={{ fontSize: "13px !important", color: "#27ae60" }} />}
                      label={isExpanded ? "Đang luyện" : "Luyện câu này"}
                      size="small"
                      sx={{
                        display: { xs: "none", sm: "inline-flex" },
                        fontWeight: 700,
                        fontSize: "11px",
                        bgcolor: isExpanded ? alpha("#27ae60", 0.15) : "transparent",
                        color: isExpanded ? "#27ae60" : "text.secondary",
                        border: `1px solid ${isExpanded ? alpha("#27ae60", 0.3) : isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                      }}
                    />

                    <IconButton
                      size="small"
                      sx={{
                        color: isExpanded ? theme.palette.primary.main : "text.secondary",
                        transform: isExpanded ? "rotate(180deg)" : "none",
                        transition: "transform 0.25s ease",
                      }}
                    >
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>

                {/* Expandable Content for this sentence only - unmountOnExit to prevent background overhead */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ p: { xs: 2, md: 3 }, pt: 1.5, borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` }}>
                    <SentencePronunciation
                      sentence={sen}
                      onResult={(result) => onPronunciationResult(index, result)}
                      existingResult={pronunciationResults[index]}
                      dataWordSearch={dataWordSearch}
                    />
                  </Box>
                </Collapse>
              </Paper>
            );
          }

          // Default 2-column layout with resizable handle bar for Normal Reading / Shadowing Mode
          return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: "24px",
                background: isDark ? alpha(theme.palette.background.paper, 0.4) : alpha("#ffffff", 0.7),
                backdropFilter: "blur(20px)",
                border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(102, 126, 234, 0.1)",
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.2)" : "0 4px 20px rgba(102, 126, 234, 0.05)",
                transition: "box-shadow 0.3s ease, border-color 0.3s ease",
                "&:hover": {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.3)" : "0 10px 30px rgba(102, 126, 234, 0.1)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  alignItems: "stretch",
                  width: "100%",
                  gap: { xs: 2.5, lg: 0 },
                }}
              >
                {/* Left Column: Sentence & Words */}
                <Box
                  sx={{
                    width: { xs: "100%", lg: `${leftWidth}%` },
                    flexShrink: 0,
                    pr: { xs: 0, lg: 1.5 },
                  }}
                >
                  <Stack spacing={2}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "8px",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: "14px",
                        }}
                      >
                        {index + 1}
                      </Box>
                      {listLabels?.[index] && (
                        <Chip
                          label={listLabels[index]}
                          size="small"
                          sx={{
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                            fontWeight: 700,
                            fontSize: "11px",
                            height: "22px",
                            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
                          }}
                        />
                      )}
                    </Box>

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: "16px",
                        bgcolor: isDark ? alpha("#fff", 0.02) : "#f8faff",
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(102, 126, 234, 0.1)"}`,
                      }}
                    >
                      {enableShadowing ? (
                        <Typography
                          sx={{
                            fontSize: "17px",
                            lineHeight: 1.6,
                            color: isDark ? "white" : "#1e293b",
                            fontWeight: 500,
                            userSelect: "text !important",
                            WebkitUserSelect: "text !important",
                            cursor: "text",
                            position: "relative",
                            zIndex: 10,
                            display: "block",
                            width: "100%",
                          }}
                        >
                          {sen}
                        </Typography>
                      ) : (
                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 0.5 }}>
                          {sen.split(" ").map((word, wordIndex) => {
                            const cleanWord = word.replace(/[,.]/g, "").toLowerCase();
                            const wordData = dataWordSearch?.items?.find((item: any) => item.word === cleanWord);
                            const ipa = wordData?.phonetic?.us;

                            return (
                              <Box
                                key={wordIndex}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  mr: 1,
                                  mb: 0.5,
                                }}
                              >
                                {showWordTooltip && wordData ? (
                                  <LightTooltip title={<WordDetail word={wordData} />}>
                                    <Typography
                                      sx={{
                                        fontSize: "17px",
                                        lineHeight: 1.2,
                                        color:
                                          showVocaLevel && wordData?.level
                                            ? colorByLevel[wordData.level as keyof typeof colorByLevel] ||
                                              (isDark ? "white" : "#1e293b")
                                            : isDark
                                              ? "white"
                                              : "#1e293b",
                                        fontWeight: 600,
                                        textAlign: "center",
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                          color: theme.palette.primary.main,
                                          transform: "scale(1.05)",
                                        },
                                      }}
                                      onMouseEnter={() => handlePlayWordSound(wordData)}
                                    >
                                      {word}
                                    </Typography>
                                  </LightTooltip>
                                ) : (
                                  <Typography
                                    sx={{
                                      fontSize: "17px",
                                      lineHeight: 1.2,
                                      color:
                                        showVocaLevel && wordData?.level
                                          ? colorByLevel[wordData.level as keyof typeof colorByLevel] ||
                                            (isDark ? "white" : "#1e293b")
                                          : isDark
                                            ? "white"
                                            : "#1e293b",
                                      fontWeight: 600,
                                      textAlign: "center",
                                    }}
                                  >
                                    {word}
                                  </Typography>
                                )}

                                {isFetchedWordSearch && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "10px",
                                      color: isDark ? "#A78BFA" : "#7C3AED",
                                      bgcolor: isDark ? alpha("#A78BFA", 0.1) : alpha("#7C3AED", 0.05),
                                      px: 0.6,
                                      py: 0.2,
                                      borderRadius: "4px",
                                      mt: 0.5,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {ipa || "-"}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Paper>
                  </Stack>
                </Box>

                {/* Resizer Divider Handle (Desktop only) */}
                <Box
                  onMouseDown={handleMouseDown}
                  sx={{
                    display: { xs: "none", lg: "flex" },
                    alignItems: "center",
                    justifyContent: "center",
                    width: "16px",
                    cursor: "col-resize",
                    userSelect: "none",
                    position: "relative",
                    zIndex: 10,
                    mx: 0.5,
                    borderRadius: "8px",
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      bgcolor: isDark ? "rgba(168, 85, 247, 0.25)" : "rgba(168, 85, 247, 0.15)",
                      "& .resizer-line": {
                        bgcolor: "#A855F7",
                        width: "3px",
                      },
                      "& .resizer-handle": {
                        bgcolor: "#A855F7",
                        color: "#fff",
                        borderColor: "#A855F7",
                      },
                    },
                  }}
                  title="Kéo để điều chỉnh độ rộng 2 cột"
                >
                  <Box
                    className="resizer-line"
                    sx={{
                      width: "2px",
                      height: "100%",
                      bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)",
                      borderRadius: "2px",
                      transition: "all 0.2s ease",
                    }}
                  />
                  <Box
                    className="resizer-handle"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "18px",
                      height: "32px",
                      borderRadius: "6px",
                      bgcolor: isDark ? "rgba(30, 41, 59, 0.95)" : "#fff",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)"}`,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "text.secondary",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <DragIndicatorIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Box>

                {/* Right Column: Main Idea & Keywords */}
                <Box
                  sx={{
                    width: { xs: "100%", lg: `calc(${100 - leftWidth}% - 24px)` },
                    flexGrow: 1,
                    pl: { xs: 0, lg: 1.5 },
                  }}
                >
                  {item ? (
                    <Stack spacing={2}>
                      {/* Main Idea */}
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "16px",
                          bgcolor: isDark ? alpha("#10B981", 0.05) : alpha("#10B981", 0.03),
                          border: `1px solid ${isDark ? alpha("#10B981", 0.15) : alpha("#10B981", 0.1)}`,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 800,
                            color: "#059669",
                            mb: 0.5,
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: 1,
                          }}
                        >
                          💡 Ý chính
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: isDark ? alpha("#fff", 0.8) : "#065F46", fontSize: "14px", lineHeight: 1.5 }}
                        >
                          {item.main_idea}
                        </Typography>
                      </Box>

                      {/* Keywords */}
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "16px",
                          bgcolor: isDark ? alpha("#F59E0B", 0.05) : alpha("#F59E0B", 0.03),
                          border: `1px solid ${isDark ? alpha("#F59E0B", 0.15) : alpha("#F59E0B", 0.1)}`,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 800,
                            color: "#D97706",
                            mb: 0.5,
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: 1,
                          }}
                        >
                          🔑 Từ khóa
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {item.keywords.map((keyword, idx) => (
                            <Chip
                              key={idx}
                              label={keyword}
                              size="small"
                              sx={{
                                background: isDark ? alpha("#F59E0B", 0.1) : "white",
                                color: "#D97706",
                                fontWeight: 700,
                                fontSize: "12px",
                                border: `1px solid ${alpha("#F59E0B", 0.2)}`,
                                "&:hover": {
                                  background: alpha("#F59E0B", 0.2),
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        minHeight: 100,
                      }}
                    >
                      <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
                        Hệ thống đang phân tích...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Fade>
  );
};

export default React.memo(MainIdeaTable);
